import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { Icon, Popup, Input, Button, Label } from "semantic-ui-react";
import CustomModal from "../../../../../configuracion/components/customModal";
import { formatMoneyWithCurrency } from "../../../../../../utils/formatter";
import { importAlertState } from "../../../../../../redux/actions/alert";
import { importUpdateTransactions } from "../../../../../../redux/actions/conciliacion";
import { ApplyMatchRecommendation, RejectMatchRecommendation } from "../../../../../../graphql/matchRecommender";
import { Obligaciones } from "../../../../../../utils/obligations";
import ObligationsDropdown from "../../../../../../containers/ObligationsDropdown/ObligationsDropdown";
import ContraparteDropdown from "../../../../../../containers/ContraparteDropdown";
import { dt } from "../../../../../../utils/documentsI18n";
import "./MatchRecommenderCandidate.scss";

/**
 * MatchRecommenderCandidate - Component for displaying Match Recommender recommendations
 * (from Zona de Entrenamiento) with purple theme and editable modal
 */
const MatchRecommenderCandidate = ({ 
  recommendation, 
  index, 
  editData, 
  history, 
  location,
  onApplied,
  onRejected,
  readOnly = false
}) => {
  const dispatch = useDispatch();
  const [isHovered, setIsHovered] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // Bloquear múltiples clics: una vez iniciada la acción, no aceptar más clics
  const applyClickedRef = useRef(false);
  
  // Get pattern and suggestion info
  const pattern = recommendation?.detected_pattern || {};
  const suggestionData = recommendation?.obligation_suggestion || {};
  const targetTx = recommendation?.target_transaction || {};
  
  // Helper para normalizar el tipo de obligación (ej: otrosingresos -> otros_ingresos; "traspaso entre cuentas" -> traspaso_entre_cuentas)
  const normalizeObligationType = (type) => {
    if (!type) return 'gastos_varios';
    const s = String(type).trim();
    const noSpaces = s.toLowerCase().replace(/\s+/g, '_');
    const typeMap = {
      'otrosingresos': 'otros_ingresos',
      'gastosvarios': 'gastos_varios',
      'gastosrrhh': 'gastos_rrhh',
      'leyessociales': 'leyes_sociales',
      'chequeporcobrar': 'cheque_por_cobrar',
      'chequeporpagar': 'cheques_por_pagar',
      'gastosdemovilizacion': 'gastos_de_movilizacion',
      'gastosdeoficina': 'gastos_de_oficina',
      'gastosdecolacion': 'gastos_de_colacion',
      'gastosdecombustible': 'gastos_de_combustible',
      'gastosdepeajes': 'gastos_de_peajes',
      'gastosdevehiculo': 'gastos_de_vehiculo',
      'gastosderepresentacion': 'gastos_de_representacion',
      'gastosdeenergiaelectrica': 'gastos_de_energia_electrica',
      'gastosdeaguapotable': 'gastos_de_agua_potable',
      'gastosdeinternetytelefonia': 'gastos_de_internet_y_telefonia',
      'traspasoentrecuentas': 'traspaso_entre_cuentas',
    };
    return typeMap[s.toLowerCase().replace(/\s/g, '')] || typeMap[noSpaces.replace(/_/g, '')] || (s.includes(' ') ? noSpaces : s);
  };
  
  // Editable fields state - inicializar con todos los datos de la recomendación (siempre usar label del catálogo para tipo)
  const [editableFields, setEditableFields] = useState(() => {
    const normType = normalizeObligationType(suggestionData?.obligation_type);
    const label = (() => {
      const found = Obligaciones.flatMap(area => area.subAreas || []).find(
        sub => sub.type === normType || sub.type?.toLowerCase() === String(normType).toLowerCase()
      );
      return found?.typeLabel || (suggestionData?.obligation_type_label && (suggestionData.obligation_type_label.includes(' ') || /[A-Z]/.test(suggestionData.obligation_type_label?.slice(1))) ? suggestionData.obligation_type_label : '') || '';
    })();
    return {
      description: suggestionData?.suggested_description || targetTx?.description || '',
      amount: targetTx?.amount || suggestionData?.suggested_amount || 0,
      obligationType: normType,
      obligationTypeLabel: label,
      obligationFamily: suggestionData?.obligation_family || 'otros_pagos_mensuales',
      obligationFamilyLabel: suggestionData?.obligation_family_label || '',
      isReceived: suggestionData?.is_received ?? (targetTx?.type === 'RECIEVED'),
      providerName: suggestionData?.suggested_provider_name || '',
      providerRut: suggestionData?.suggested_provider_rut || '',
      providerId: '',
    };
  });

  // Obtener companyId de la recomendación
  const companyId = recommendation?.company_id || '';

  const { executeMutation: applyRecommendation, fetching: applyFetching } = ApplyMatchRecommendation();
  const { executeMutation: rejectRecommendation, fetching: rejectFetching } = RejectMatchRecommendation();

  const handleObligationTypeDropdownChange = (e, data) => {
    // Siempre usar el tipo como string (clave), nunca typeKey numérico. Normalizar para que el backend reciba snake_case (ej: gastos_varios).
    const rawType = data?.obligation
      ? (typeof data.obligation.type === 'string' ? data.obligation.type : (typeof data.value === 'string' ? data.value : null))
      : (typeof data?.value === 'string' ? data.value : data);
    const obligationTypeStr = rawType != null && rawType !== '' ? normalizeObligationType(String(rawType)) : null;
    if (obligationTypeStr == null) return;

    if (data?.obligation) {
      const obl = data.obligation;
      setEditableFields(prev => ({
        ...prev,
        obligationType: obligationTypeStr,
        obligationTypeLabel: obl.typeLabel || prev.obligationTypeLabel,
        obligationFamily: typeof obl.family === 'string' ? obl.family : (prev.obligationFamily),
        obligationFamilyLabel: obl.familyLabel || prev.obligationFamilyLabel,
      }));
    } else {
      const found = Obligaciones
        .flatMap(area => area.subAreas || [])
        .find(sub => sub.type === obligationTypeStr || sub.type === rawType);
      setEditableFields(prev => ({
        ...prev,
        obligationType: obligationTypeStr,
        obligationTypeLabel: found?.typeLabel || prev.obligationTypeLabel,
        obligationFamily: found?.family || prev.obligationFamily,
        obligationFamilyLabel: found?.familyLabel || prev.obligationFamilyLabel,
      }));
    }
  };

  const handleProviderDropdownChange = (e, data) => {
    setEditableFields(prev => ({
      ...prev,
      providerName: data?.name || data?.text || '',
      providerId: data?.value || '',
      providerRut: data?.rut || '',
    }));
  };

  // Generate obligation type options from Obligaciones util (para compatibilidad)
  // Obligaciones is an array with areas that contain subAreas
  const OBLIGATION_TYPE_OPTIONS = Obligaciones
    .flatMap(area => area.subAreas || [])
    .filter(subArea => {
      // Filtrar solo tipos que se pueden crear manualmente (no facturas de SII)
      const excludedTypes = [
        'factura_proveedores', 'factura_clientes', 'nota_credito_proveedores', 
        'nota_credito_clientes', 'nota_debito_proveedores', 'nota_debito_clientes',
        'boleta_proveedores', 'boleta_clientes'
      ];
      return !excludedTypes.includes(subArea.type);
    })
    .map(subArea => ({
      key: subArea.type,
      value: subArea.type,
      text: subArea.typeLabel,
      family: subArea.family,
    }));

  // Helper para obtener el label correcto de un tipo de obligación
  // Busca en el catálogo para manejar casos donde obligation_type_label está mal guardado
  const getObligationTypeLabel = (type, fallbackLabel) => {
    if (!type) return fallbackLabel || 'Crear obligación';
    
    // Normalizar el tipo (quitar espacios, lowercase)
    const normalizedType = type.toLowerCase().replace(/\s+/g, '_');
    
    // Buscar en el catálogo
    const found = OBLIGATION_TYPE_OPTIONS.find(opt => 
      opt.key === type || 
      opt.key === normalizedType ||
      opt.key.replace(/_/g, '') === type.replace(/_/g, '') // comparar sin guiones bajos
    );
    
    if (found) return found.text;
    
    // Si el fallbackLabel parece un label real (tiene espacios o mayúsculas), usarlo
    if (fallbackLabel && (fallbackLabel.includes(' ') || /[A-Z]/.test(fallbackLabel.slice(1)))) {
      return fallbackLabel;
    }
    
    // Último recurso: capitalizar el tipo
    return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
  };

  // Usar pattern, suggestionData y targetTx ya definidos arriba
  const suggestion = suggestionData; // Alias para compatibilidad

  // Check if transaction is received type (ABONO)
  // Priorizar el tipo real de la transacción, luego is_received de la sugerencia, luego pattern
  const isReceived = targetTx?.type === 'RECIEVED' || suggestion?.is_received === true || pattern?.transaction_type === 'RECIEVED';

  const isApplied = (recommendation?.status || '').toLowerCase() === 'applied';
  const isDisabled = isApplying || applyFetching || rejectFetching || readOnly || isApplied;

  // Generate avatar initials based on obligation type label
  const getAvatarInitials = () => {
    // Usar el label correcto del catálogo
    const label = getObligationTypeLabel(suggestion?.obligation_type, suggestion?.obligation_type_label);
    if (!label || label.length === 0) return 'MR';
    
    // If it's a single word, take first 2 characters
    const words = label.trim().split(/\s+/);
    if (words.length === 1) {
      return label.substring(0, 2).toUpperCase();
    }
    // If multiple words, take first letter of first 2 words
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const formatMoney = (amount) => formatMoneyWithCurrency(Math.abs(amount), 'CLP');

  // Format amount - parentheses for SPENT (egreso), no parentheses for RECIEVED (ingreso)
  const formatAmount = (amount, isReceived) => {
    const formatted = formatMoney(amount);
    // Egreso = paréntesis, Ingreso = sin paréntesis
    if (!isReceived) {
      return `(${formatted})`;
    }
    return formatted;
  };

  // Handler for opening edit modal (solo abre el popup de edición)
  const handleOpenEditModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDisabled) return;
    
    // Reset editable fields to current values from recommendation (label del catálogo para que el dropdown muestre "Traspaso entre Cuentas", no el type)
    const normType = normalizeObligationType(suggestion?.obligation_type);
    const typeLabel = getObligationTypeLabel(normType, suggestion?.obligation_type_label || '');
    setEditableFields({
      description: suggestion?.suggested_description || targetTx?.description || '',
      amount: targetTx?.amount || suggestion?.suggested_amount || 0,
      obligationType: normType,
      obligationTypeLabel: typeLabel,
      obligationFamily: suggestion?.obligation_family || 'otros_pagos_mensuales',
      obligationFamilyLabel: suggestion?.obligation_family_label || '',
      isReceived: suggestion?.is_received ?? (targetTx?.type === 'RECIEVED'),
      providerName: suggestion?.suggested_provider_name || '',
      providerRut: suggestion?.suggested_provider_rut || '',
      providerId: '',
    });
    setShowEditModal(true);
  };

  // Aplicar recomendación sin abrir modal (conciliar directo con datos de la recomendación)
  const handleApplyImmediate = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (applyClickedRef.current || isDisabled || !recommendation?.id) return;
    applyClickedRef.current = true;
    setIsApplying(true);
    try {
      const obligationType = normalizeObligationType(suggestion?.obligation_type);
      const input = {
        recommendation_id: recommendation.id,
        create_obligation: true,
        obligation_description: suggestion?.suggested_description || targetTx?.description || '',
        obligation_type_override: obligationType != null && obligationType !== '' ? String(obligationType) : undefined,
        // Monto no se envía: el backend usa siempre el de la recomendación/transacción
      };
      const { data: response, error } = await applyRecommendation({ input });
      if (error) {
        dispatch(importAlertState({ content: `Error al aplicar la recomendación: ${error.message}`, state: 'error', duration: 4 }));
        applyClickedRef.current = false;
        setIsApplying(false);
        return;
      }
      if (response?.applyMatchRecommendation?.success) {
        dispatch(importUpdateTransactions(true));
        dispatch(importAlertState({ content: 'Recomendación aplicada exitosamente. Se creó la obligación y se concilió.', state: 'success', duration: 3 }));
        if (onApplied) {
          const result = onApplied(recommendation.id);
          if (result && typeof result.then === 'function') await result;
        }
      } else {
        dispatch(importAlertState({ content: response?.applyMatchRecommendation?.message || 'Error al aplicar la recomendación', state: 'error', duration: 4 }));
        applyClickedRef.current = false;
      }
    } catch (err) {
      console.error('[MatchRecommenderCandidate] Error applying recommendation:', err);
      dispatch(importAlertState({ content: 'Error al aplicar la recomendación.', state: 'error', duration: 4 }));
      applyClickedRef.current = false;
    } finally {
      setIsApplying(false);
    }
  };

  // Handler for applying recommendation (desde el modal: Tipo, Descripción y Proveedor editables; Monto fijo)
  const handleApply = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (applyClickedRef.current || isDisabled || !recommendation?.id) return;

    applyClickedRef.current = true;
    setIsApplying(true);

    try {
      // Tipo: siempre enviar el valor actual del formulario (o sugerencia como fallback) para que persista
      const rawType = (editableFields.obligationType != null && String(editableFields.obligationType).trim() !== '')
        ? String(editableFields.obligationType).trim()
        : (suggestion?.obligation_type ? String(suggestion.obligation_type).trim() : '');
      const obligationTypeOverride = rawType ? normalizeObligationType(rawType) : undefined;

      const input = {
        recommendation_id: recommendation.id,
        create_obligation: true,
        obligation_description: editableFields.description || undefined,
        obligation_type_override: obligationTypeOverride,
        obligation_provider_name_override: (editableFields.providerName && String(editableFields.providerName).trim())
          ? String(editableFields.providerName)
          : undefined,
        obligation_provider_rut_override: (editableFields.providerRut && String(editableFields.providerRut).trim())
          ? String(editableFields.providerRut)
          : undefined,
      };

      const { data: response, error } = await applyRecommendation({ input });

      if (error) {
        console.error('[MatchRecommenderCandidate] Error applying recommendation:', error);
        applyClickedRef.current = false;
        dispatch(
          importAlertState({
            content: `Error al aplicar la recomendación: ${error.message}`,
            state: "error",
            duration: 4,
          })
        );
        setIsApplying(false);
        setShowEditModal(false);
        return;
      }

      if (response?.applyMatchRecommendation?.success) {
        dispatch(importUpdateTransactions(true));
        dispatch(
          importAlertState({
            content: `Recomendación aplicada exitosamente. Se creó la obligación y se concilió.`,
            state: "success",
            duration: 3,
          })
        );
        // Esperar al refetch de recomendaciones para que la UI actualice la info de la obligación sugerida
        if (onApplied) {
          const result = onApplied(recommendation.id);
          if (result && typeof result.then === 'function') await result;
        }
      } else {
        applyClickedRef.current = false;
        dispatch(
          importAlertState({
            content: response?.applyMatchRecommendation?.message || 'Error al aplicar la recomendación',
            state: "error",
            duration: 4,
          })
        );
      }
    } catch (error) {
      console.error('[MatchRecommenderCandidate] Error applying recommendation:', error);
      applyClickedRef.current = false;
      dispatch(
        importAlertState({
          content: `Error al aplicar la recomendación.`,
          state: "error",
          duration: 4,
        })
      );
    } finally {
      setIsApplying(false);
      setShowEditModal(false);
    }
  };

  // Handler for rejecting recommendation
  const handleReject = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isDisabled || !recommendation?.id) return;

    try {
      const input = {
        recommendation_id: recommendation.id,
        rejection_reason: 'Rechazado por el usuario desde conciliación bancaria',
      };

      const { data: response, error } = await rejectRecommendation({ input });

      if (error) {
        console.error('[MatchRecommenderCandidate] Error rejecting recommendation:', error);
        dispatch(
          importAlertState({
            content: `Error al rechazar la recomendación.`,
            state: "error",
            duration: 4,
          })
        );
        return;
      }

      dispatch(
        importAlertState({
          content: `Recomendación rechazada.`,
          state: "info",
          duration: 2,
        })
      );

      if (onRejected) onRejected(recommendation.id);
      
      // Trigger refetch
      dispatch(importUpdateTransactions(true));
    } catch (error) {
      console.error('[MatchRecommenderCandidate] Error rejecting recommendation:', error);
    }
  };

  // Calcular el score de confianza para mostrar visualmente
  const confidenceScore = recommendation?.confidence_score;
  const confidencePercent = confidenceScore ? Math.round(confidenceScore * 100) : null;
  const confidenceColor = confidencePercent 
    ? confidencePercent >= 80 ? '#10b981' // verde
    : confidencePercent >= 60 ? '#f59e0b' // naranja
    : '#ef4444' // rojo
    : '#6b7280'; // gris

  // Obtener información del proveedor si existe
  const providerName = suggestion?.suggested_provider_name || pattern?.provider_name;
  const providerRut = suggestion?.suggested_provider_rut || pattern?.provider_rut;
  const hasProviderInfo = providerName || providerRut;

  // Obtener la familia de obligación para contexto adicional
  const obligationFamilyLabel = suggestion?.obligation_family_label;

  // Obtener fecha de la transacción
  const txDate = targetTx?.date ? moment(targetTx.date) : null;

  // Construir la descripción completa - usar descripción real de la transacción
  // Priorizar la descripción completa de la transacción sobre suggested_description (que es truncada)
  const description = targetTx?.description || suggestion?.suggested_description || pattern?.description_keywords?.join(', ') || '';
  const fullDescription = hasProviderInfo 
    ? `Emitida por ${providerName || ''}${providerRut && !providerName ? providerRut : ''} :: ${description}`
    : description;

  return (
    <div className={`match-recommender-container ${isDisabled ? 'match-recommender-container--disabled' : ''}`}>
      <div className="match-recommender-label">
        Recomendación IA
        <Popup
          trigger={
            <Icon
              name="info circle"
              size="small"
              className="match-recommender-label-icon"
            />
          }
          content="Recomendación basada en patrones de conciliación aprendidos en Zona de Entrenamiento"
          inverted
          position="top center"
          size="tiny"
        />
      </div>
      <div 
        className={`match-recommender-item ${isApplied ? 'match-recommender-item--applied' : ''}`}
        onMouseEnter={() => !isDisabled && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={isApplied ? undefined : handleApplyImmediate}
      >
        <div className="match-recommender-avatar">
          {getAvatarInitials()}
        </div>
        <div className="match-recommender-body">
          <div className="match-recommender-header">
            <div className="match-recommender-type">
              {getObligationTypeLabel(suggestion?.obligation_type, suggestion?.obligation_type_label)}
              {isApplied && (
                <span className="match-recommender-applied-badge">Aplicada</span>
              )}
            </div>
            <div className="match-recommender-time-container">
              {txDate && (
                <div className="match-recommender-time">
                  {dt('reconcile.issuedOn')} {txDate.format('DD/MM/YY')}
                </div>
              )}
              {isHovered && !isDisabled && (
                <div className="match-recommender-reconcile-hint">
                  <span className="match-recommender-reconcile-text">{dt('reconcile.reconcile')}</span>
                  <Icon name="arrow up right" className="match-recommender-reconcile-icon" style={{ transform: 'rotate(45deg)' }} />
                </div>
              )}
            </div>
            <div className={`match-recommender-amount-container ${isReceived ? 'match-recommender-amount-container--given' : 'match-recommender-amount-container--received'}`}>
              <span className="match-recommender-amount-text">
                {formatAmount(targetTx?.amount || suggestion?.suggested_amount || 0, isReceived)}
              </span>
              {!readOnly && !isApplied && (
                <div className="match-recommender-actions-inline">
                  <Popup
                    trigger={
                      <div
                        className="match-recommender-edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditModal(e);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenEditModal(e); } }}
                      >
                        <Icon name="edit outline" />
                      </div>
                    }
                    content="Editar obligación antes de conciliar"
                    position="top center"
                    size="tiny"
                    inverted
                  />
                  <Popup
                    trigger={
                      <div
                        className="match-recommender-reject-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(e);
                        }}
                      >
                        {rejectFetching ? (
                          <div className="match-recommender-small-spinner match-recommender-small-spinner--reject"></div>
                        ) : (
                          <Icon name="trash alternate outline" />
                        )}
                      </div>
                    }
                    content="Rechazar esta recomendación"
                    position="top center"
                    size="tiny"
                    inverted
                  />
                </div>
              )}
            </div>
          </div>
          {/* Descripción principal - formato igual a MatchItem */}
          <div className="match-recommender-text">
            {fullDescription || 'Sin descripción'}
          </div>
        </div>
      </div>

      {/* Edit Modal: centrado y con scroll interno (sidebar en el modal) */}
      <CustomModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        size="large"
        dimmer="blurring"
        classNameModal="match-recommender-modal match-recommender-modal--centered"
        classNameTitle="match-recommender-modal-header"
        title="Confirmar Recomendación IA"
        classNameContent="match-recommender-modal-content match-recommender-modal-content--scrollable"
        scrolling
        content={
          <>
          {/* Sección: Obligación Sugerida - Tipo, Descripción y Proveedor editables; Monto solo lectura */}
          <div className="modal-section obligation-section">
            <div className="section-title">
              <Icon name="file alternate outline" /> Obligación Sugerida
            </div>
            <div className="obligation-fields-row">
              <div className="obligation-field obligation-field--full">
                <span className="label">TIPO DE OBLIGACIÓN:</span>
                <ObligationsDropdown
                  value={editableFields.obligationType}
                  type={editableFields.obligationTypeLabel || getObligationTypeLabel(editableFields.obligationType, '')}
                  onChange={handleObligationTypeDropdownChange}
                  placeholder="Selecciona obligación"
                  className="suggestion-dropdown"
                />
              </div>
              <div className="obligation-field obligation-field--full">
                <span className="label">DESCRIPCIÓN:</span>
                <Input
                  fluid
                  value={editableFields.description}
                  onChange={(e) => setEditableFields(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descripción de la obligación"
                />
              </div>
              <div className="obligation-field obligation-field--full">
                <span className="label">PROVEEDOR:</span>
                <ContraparteDropdown
                  id={`provider-recommendation-${recommendation?.id || 'modal'}`}
                  value={editableFields.providerId || ''}
                  onChange={handleProviderDropdownChange}
                  placeholder="Proveedor o cliente"
                  companyId={companyId}
                  allowCreate={false}
                  selectedTextOverride={editableFields.providerName || ''}
                  className="suggestion-dropdown"
                />
              </div>
              <div className="obligation-field obligation-field--full">
                <span className="label">MONTO:</span>
                <span className="value value--readonly">{formatMoney(Math.abs(editableFields.amount))}</span>
              </div>
            </div>
          </div>

          {/* Sección: Patrón Detectado y Estadísticas */}
          <div className="modal-section pattern-section">
            <div className="pattern-grid">
              {/* Columna izquierda: Patrón */}
              <div className="pattern-column">
                <div className="section-title">
                  <Icon name="search" /> Patrón Detectado
                </div>
                <div className="pattern-info">
                  {pattern?.description_keywords && pattern.description_keywords.length > 0 ? (
                    <div className="info-item">
                      <span className="label">Keywords:</span>
                      <span className="value keywords-value">
                        {pattern.description_keywords.map((kw, i) => (
                          <Label key={i} size="tiny" basic>{kw}</Label>
                        ))}
                      </span>
                    </div>
                  ) : (
                    <div className="info-item">
                      <span className="label">Keywords:</span>
                      <span className="value">—</span>
                    </div>
                  )}
                  <div className="info-item">
                    <span className="label">Tipo Tx:</span>
                    <span className="value">
                      {pattern?.transaction_type ? (pattern.transaction_type === 'RECIEVED' ? 'Ingreso' : 'Egreso') : '—'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Rango Monto:</span>
                    <span className="value">
                      {(pattern?.amount_min != null || pattern?.amount_max != null)
                        ? `${formatMoney(pattern?.amount_min ?? 0)} - ${formatMoney(pattern?.amount_max ?? 0)}`
                        : '—'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Columna derecha: Estadísticas */}
              <div className="stats-column">
                <div className="section-title">
                  <Icon name="chart bar" /> Estadísticas
                </div>
                <div className="stats-info">
                  <div className="info-item">
                    <span className="label">Ocurrencias:</span>
                    <span className="value">
                      {recommendation?.stats?.occurrence_count ?? '—'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Confianza:</span>
                    <span className="value confidence-value">
                      {recommendation?.confidence_score != null
                        ? `${Math.round(recommendation.confidence_score * 100)}%`
                        : '—'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Monto Promedio:</span>
                    <span className="value">
                      {recommendation?.stats?.avg_amount != null
                        ? formatMoney(recommendation.stats.avg_amount)
                        : '—'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Razón de la recomendación */}
          {recommendation?.recommendation_reason && (
            <div className="modal-section reason-section">
              <div className="section-title">
                <Icon name="lightbulb outline" /> Razón de la Recomendación
              </div>
              <p className="reason-text">{recommendation.recommendation_reason}</p>
            </div>
          )}
          </>
        }
        classNameActions="match-recommender-modal-actions"
        customActions={
          <>
          <Button
            basic
            onClick={() => setShowEditModal(false)}
            className="cancel-btn"
          >
            Cancelar
          </Button>
          <Button
            primary
            className="apply-btn"
            onClick={handleApply}
            disabled={isApplying}
          >
            {isApplying ? 'Guardando...' : 'Guardar cambios'}
          </Button>
          </>
        }
      />
    </div>
  );
};

export default MatchRecommenderCandidate;